import { Button } from '@shared/components';
import { CircularProgress } from '@shared/components/plugin-components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useExtensionMessagingContext } from '@shared/contexts/useExtensionMessagingContext';
import { usePageInfo } from '@shared/features/sidekick-navigator/PageInfoContextProvider';
import {
  canBuildShipment,
  getTMSShipmentUrls,
} from '@shared/features/sidekick-navigator/tms';
import { isTmsVerificationSupported } from '@shared/features/sidekick-navigator/tmsVerification';
import { useUpdateShipmentMutation } from '@shared/generated/graphql';
import { useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import { toTMS } from './TMSSidekick';
import { getRefetchDomPromise } from './refetchDom';

type RefreshShipmentButtonProps = {
  parentUrl: string;
  referenceNumber: string;
};

export function RefreshShipmentButton({
  parentUrl,
  referenceNumber,
}: RefreshShipmentButtonProps) {
  const { defaultOrgId } = useMeContext();
  const { pageInfo } = usePageInfo();
  const { sendToast } = useToast();
  const { refetchDom } = useExtensionMessagingContext();
  const [updateShipment, { loading: mutationLoading }] =
    useUpdateShipmentMutation();
  const [domFetchLoading, setDomFetchLoading] = useState<boolean>(false);
  const tms = toTMS(pageInfo);

  async function buildShipmentFromDom() {
    if (!defaultOrgId) {
      sendToast('No organization found');
      return;
    }
    if (!referenceNumber) {
      sendToast('No shipment reference number found');
      return;
    }

    const shipmentUrls = getTMSShipmentUrls(tms, parentUrl, referenceNumber);
    const domTimeout = shipmentUrls === undefined ? 1000 : 10000;

    const p = {
      domTimeout,
      shipmentUrls,
      refetchDom,
    };

    setDomFetchLoading(true);
    const doms = await getRefetchDomPromise(p).finally(() =>
      setDomFetchLoading(false)
    );

    const domContent = doms?.join('\n') || '';

    await updateShipment({
      variables: {
        input: {
          organizationId: defaultOrgId,
          externalId: referenceNumber,
          data: domContent,
          integration: tms,
        },
      },
      refetchQueries: ['FindShipment'],
    }).catch((e) => {
      sendToast('Failed to refresh shipment', {
        description: e.message,
        variant: 'error',
      });
    });
  }

  const loading = mutationLoading || domFetchLoading;

  const DOM_LOADING_MESSAGE = 'Retrieving load details...';
  const MUTATION_LOADING_MESSAGE = 'Building shipment...';
  const REFRESH_SHIPMENT_MESSAGE = 'Recreate shipment';

  const buttonText = domFetchLoading
    ? DOM_LOADING_MESSAGE
    : mutationLoading
    ? MUTATION_LOADING_MESSAGE
    : REFRESH_SHIPMENT_MESSAGE;

  if (!canBuildShipment(pageInfo)) return null;
  if (isTmsVerificationSupported(tms)) return null;

  return (
    <div className="py-2">
      <Button
        size="xs"
        icon={
          loading ? (
            <div className="mr-2">
              <CircularProgress size="sm" />
            </div>
          ) : (
            <MdRefresh />
          )
        }
        tooltip={`Recreate Shipment from TMS`}
        onPress={buildShipmentFromDom}
      >
        {buttonText}
      </Button>
    </div>
  );
}
